import { register } from "./registry";

/**
 * Live duplicate warning on a master's name box (CargoBooks checks the name as soon as you leave the box).
 *
 *   <input name="Name" data-kit-dup="/api/v1/units/autocomplete" data-kit-dup-self="@Model.Id" data-kit-dup-label="unit">
 *   data-kit-dup-param-place_id="PlaceId"   adds ?place_id=<value of the form's PlaceId> (e.g. godown names are per place)
 *
 * Leaving the box looks the typed name up with the master's own autocomplete; an exact match (any case, extra spaces
 * ignored) on another record shows a note under the box. It only warns: Save still refuses a real duplicate.
 */
type Item = { id: string; text: string; secondary?: string };

const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();

function noteFor(input: HTMLInputElement): HTMLElement {
  let note = input.parentElement?.querySelector<HTMLElement>(":scope > .kit-dup-note") ?? null;
  if (!note) {
    note = document.createElement("span");
    note.className = "kit-dup-note";
    note.setAttribute("role", "status");
    note.hidden = true;
    input.insertAdjacentElement("afterend", note);
  }
  return note;
}

register((root) => {
  root.querySelectorAll<HTMLInputElement>("input[data-kit-dup]").forEach((input) => {
    if (input.dataset.kitDupBound === "1") return;
    input.dataset.kitDupBound = "1";
    let seq = 0;

    const check = async () => {
      const value = input.value;
      const note = noteFor(input);
      if (!norm(value)) { note.hidden = true; input.removeAttribute("aria-invalid"); return; }
      const url = new URL(input.dataset.kitDup!, location.origin);
      url.searchParams.set("q", value.trim());
      url.searchParams.set("limit", "50");
      for (const [key, raw] of Object.entries(input.dataset)) {
        if (!key.startsWith("kitDupParam") || !raw) continue;
        const param = key.slice("kitDupParam".length).replace(/^./, (c) => c.toLowerCase()).replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());
        const field = (input.form ?? document).querySelector<HTMLInputElement>(`[name="${raw}"]`);
        if (field?.value) url.searchParams.set(param, field.value);
      }
      const mine = ++seq;
      try {
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok || mine !== seq) return;
        const items = (await res.json()) as Item[];
        const self = (input.dataset.kitDupSelf ?? "").toLowerCase();
        const clash = items.find((it) => norm(it.text) === norm(value) && it.id.toLowerCase() !== self);
        if (clash) {
          note.textContent = `${input.dataset.kitDupLabel ?? "A record"} named "${clash.text}"${clash.secondary ? ` (${clash.secondary})` : ""} already exists`;
          note.hidden = false;
          input.setAttribute("aria-invalid", "true");
        } else {
          note.hidden = true;
          input.removeAttribute("aria-invalid");
        }
      } catch { /* offline or not allowed: Save still checks */ }
    };

    input.addEventListener("change", () => { void check(); });
    input.addEventListener("input", () => { noteFor(input).hidden = true; input.removeAttribute("aria-invalid"); });
  });
});
